import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import './Profile.css';

const Profile = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const isAr = i18n.language === 'ar';

    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (passwords.newPassword !== passwords.confirmPassword) {
            setMessage({ type: 'error', text: t('profile.passwordMismatch') });
            return;
        }

        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await api.post('/auth/change-password', {
                currentPassword: passwords.currentPassword,
                newPassword: passwords.newPassword
            });

            if (response.data.success) {
                setMessage({ type: 'success', text: t('profile.passwordSuccess') });
                setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setShowPasswords({ current: false, new: false, confirm: false });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.message || t('common.error')
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="profile-page fade-in">
            <div className="page-header">
                <h1>{t('profile.title')}</h1>
            </div>

            <div className="profile-grid">
                <div className="card profile-info-card">
                    <div className="info-header">
                        <span className="material-icons info-icon">person</span>
                        <h3>{t('profile.personalInfo')}</h3>
                    </div>

                    <div className="info-content">
                        <div className="info-item">
                            <label>{t('profile.fullNameAr')}</label>
                            <span>{user?.fullNameAr || user?.name}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('profile.fullNameEn')}</label>
                            <span>{user?.fullNameEn || user?.name}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('profile.email')}</label>
                            <span>{user?.email}</span>
                        </div>
                        <div className="info-item">
                            <label>{t('profile.username')}</label>
                            <span className="badge-pill">{user?.username}</span>
                        </div>
                    </div>
                </div>

                <div className="card password-card">
                    <div className="info-header">
                        <span className="material-icons info-icon">lock</span>
                        <h3>{t('profile.changePassword')}</h3>
                    </div>

                    <form onSubmit={handlePasswordChange}>
                        {message.text && (
                            <div className={`alert alert-${message.type}`}>
                                {message.text}
                            </div>
                        )}

                        <div className="form-group">
                            <label>{t('profile.currentPassword')}</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPasswords.current ? "text" : "password"}
                                    className="form-control"
                                    value={passwords.currentPassword}
                                    onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle-icon"
                                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                                >
                                    <span className="material-icons">
                                        {showPasswords.current ? 'visibility_off' : 'visibility'}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{t('profile.newPassword')}</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPasswords.new ? "text" : "password"}
                                    className="form-control"
                                    value={passwords.newPassword}
                                    onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle-icon"
                                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                                >
                                    <span className="material-icons">
                                        {showPasswords.new ? 'visibility_off' : 'visibility'}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{t('profile.confirmPassword')}</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPasswords.confirm ? "text" : "password"}
                                    className="form-control"
                                    value={passwords.confirmPassword}
                                    onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle-icon"
                                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                >
                                    <span className="material-icons">
                                        {showPasswords.confirm ? 'visibility_off' : 'visibility'}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? t('common.loading') : t('profile.updatePassword')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Profile;
